import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Label as LabelIcon, Archive as ArchiveIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PageLayout from './PageLayout';
import { getContrastText } from '../utils/colorUtils';
import { alpha } from '@mui/material/styles';
import { hasReachedLimit } from '../config/planLimits';
import PlanComparisonDialog from './shared/PlanComparisonDialog';
import PlanInfoBanner from './shared/PlanInfoBanner';

// Categorias padrão
const DEFAULT_CATEGORIES = [
  'Lembretes',
  'Finanças',
  'Saúde',
  'Alimentação',
  'Trabalho',
  'Estudos',
  'Pessoal',
  'Outros'
];

const PILLARS = [
  { id: 'vitalidade', label: 'Vitalidade' },
  { id: 'mentalidade', label: 'Mentalidade' },
  { id: 'prosperidade', label: 'Prosperidade' }
];

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100'
};

const getPillarColor = (pillarId, customColors) => {
  const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
  return customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id] || DEFAULT_PILLAR_COLORS.vitalidade;
};

const getPillarLabel = (pillarId) => {
  const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
  const found = PILLARS.find(p => p.id === id);
  return found ? found.label : PILLARS[0].label;
};

export default function Notes({ customColors, embeddedInCentral, hideAddButtonInHeader }) {
  const [notes, setNotes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const { currentUser } = useAuth();
  const { userPlan } = usePlan();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [pillarFilter, setPillarFilter] = useState('all');
  const [currentFilter, setCurrentFilter] = useState('all');
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState('vitalidade');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [errors, setErrors] = useState({ title: false, content: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Referência estática para handleOpenDialog
  Notes.onAdd = () => {
    // Verifica se o usuário está no plano básico e já atingiu o limite
    if (hasReachedLimit(userPlan, 'maxNotes', notes.length)) {
      setShowPlanDialog(true);
      return;
    }
    setSelectedDate(new Date());
    setNoteTitle('');
    setNoteContent('');
    setSelectedCategory('');
    setSelectedPillar('vitalidade');
    setEditingNote(null);
    setOpenDialog(true);
  };

  useEffect(() => {
    loadNotes();
  }, [currentUser]);

  const loadNotes = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Carregar categorias personalizadas
        if (userData.categories) {
          setCategories([...DEFAULT_CATEGORIES, ...userData.categories]);
        }
        // Ordenar notas por data, mais antigas primeiro
        const sortedNotes = [...(userData.notes || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
        setNotes(sortedNotes);
      }
    } catch (error) {
      console.error('Erro ao carregar anotações:', error);
    }
  };

  const handleEditNote = (note) => {
    setSelectedDate(new Date(note.date));
    setNoteTitle(note.title || '');
    setNoteContent(note.content || '');
    setSelectedCategory(note.category || '');
    setSelectedPillar(note.pillar || 'vitalidade');
    setEditingNote(note);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
    setSelectedCategory('');
    setSelectedPillar('vitalidade');
  };

  const handleSave = async () => {
    // Reset errors
    setErrors({ title: false, content: false });
    
    // Validate fields
    const newErrors = {
      title: !noteTitle.trim(),
      content: !noteContent.trim()
    };

    if (newErrors.title || newErrors.content) {
      setErrors(newErrors);
      setSnackbar({
        open: true,
        message: 'Por favor, preencha todos os campos obrigatórios',
        severity: 'error'
      });
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      let updatedNotes;

      if (editingNote) {
        // Atualizar nota existente
        updatedNotes = notes.map(note => 
          note.id === editingNote.id 
            ? { 
                ...note, 
                title: noteTitle,
                content: noteContent,
                date: selectedDate.toISOString(),
                category: selectedCategory,
                pillar: selectedPillar,
                updatedAt: new Date().toISOString()
              }
            : note
        );
      } else {
        // Criar nova nota
        const newNote = {
          id: Date.now().toString(),
          title: noteTitle,
          content: noteContent,
          date: selectedDate.toISOString(),
          category: selectedCategory,
          pillar: selectedPillar,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isImportant: false,
          isArchived: false
        };
        updatedNotes = [...notes, newNote];
      }

      // Ordenar notas por data
      updatedNotes.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Salvar notas e categorias personalizadas
      await updateDoc(userRef, { 
        notes: updatedNotes,
        categories: categories.filter(cat => !DEFAULT_CATEGORIES.includes(cat))
      });

      // Atualizar estado local
      setNotes(updatedNotes);
      
      // Limpar formulário e fechar diálogo
      setNoteTitle('');
      setNoteContent('');
      setSelectedCategory('');
      setSelectedPillar('vitalidade');
      setSelectedDate(new Date());
      setEditingNote(null);
      setOpenDialog(false);

      setSnackbar({
        open: true,
        message: 'Nota salva com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao salvar nota:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar a nota. Por favor, tente novamente.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim() || categories.includes(newCategory.trim())) {
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const updatedCategories = [...categories, newCategory.trim()];
      await updateDoc(userRef, { 
        categories: updatedCategories.filter(cat => !DEFAULT_CATEGORIES.includes(cat)) 
      });
      setCategories(updatedCategories);
      setSelectedCategory(newCategory.trim());
      setNewCategory('');
    } catch (error) {
      console.error('Erro ao adicionar categoria:', error);
    }
  };

  const handleDeleteCategory = async (categoryToDelete) => {
    // Não permite excluir categorias padrão
    if (DEFAULT_CATEGORIES.includes(categoryToDelete)) {
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Remove a categoria da lista
      const updatedCategories = categories.filter(cat => cat !== categoryToDelete);
      
      // Remove a categoria de todas as notas que a usam
      const updatedNotes = notes.map(note => 
        note.category === categoryToDelete 
          ? { ...note, category: '' }
          : note
      );
      
      // Salva no Firebase
      await updateDoc(userRef, { 
        categories: updatedCategories.filter(cat => !DEFAULT_CATEGORIES.includes(cat)),
        notes: updatedNotes
      });
      
      // Atualiza o estado local
      setCategories(updatedCategories);
      setNotes(updatedNotes);
      
      // Se a categoria excluída estava selecionada, limpa a seleção
      if (selectedCategory === categoryToDelete) {
        setSelectedCategory('');
      }
      
      setSnackbar({
        open: true,
        message: 'Categoria excluída com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir categoria. Por favor, tente novamente.',
        severity: 'error'
      });
    }
  };

  const filteredNotes = filterCategory
    ? notes.filter(note => note.category === filterCategory)
    : notes;

  const notesFilteredByPillar =
    pillarFilter === 'all'
      ? filteredNotes
      : filteredNotes.filter(n => (n.pillar || 'vitalidade') === pillarFilter);

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Se noteToDelete.title é 'todas as notas', removemos todas
      if (noteToDelete.title === 'todas as notas') {
        await updateDoc(userRef, { notes: [] });
        setNotes([]);
      } else {
        // Remove apenas a nota específica
        const updatedNotes = notes.filter(note => note.id !== noteToDelete.id);
        await updateDoc(userRef, { notes: updatedNotes });
        setNotes(updatedNotes);
      }
      
      setShowDeleteDialog(false);
      setNoteToDelete(null);
      
      // Mostrar mensagem de sucesso
      setSnackbar({
        open: true,
        message: noteToDelete.title === 'todas as notas' 
          ? 'Todas as notas foram removidas com sucesso!'
          : 'Nota removida com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao excluir anotação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir. Por favor, tente novamente.',
        severity: 'error'
      });
    }
  };

  const getFilteredNotes = () => {
    const base = notesFilteredByPillar;
    if (currentFilter === 'all') {
      return base;
    } else if (currentFilter === 'important') {
      return base.filter(note => note.isImportant);
    } else if (currentFilter === 'archived') {
      return base.filter(note => note.isArchived);
    }
    return [];
  };

  const handleFilterChange = (event, newValue) => {
    setCurrentFilter(newValue);
  };

  const handleToggleImportant = async (noteId) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const updatedNotes = notes.map(note => 
        note.id === noteId 
          ? { ...note, isImportant: !note.isImportant }
          : note
      );
      await updateDoc(userRef, { notes: updatedNotes });
      setNotes(updatedNotes);
    } catch (error) {
      console.error('Erro ao marcar/desmarcar nota como importante:', error);
    }
  };

  const handleToggleArchive = async (noteId) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const updatedNotes = notes.map(note => 
        note.id === noteId 
          ? { ...note, isArchived: !note.isArchived }
          : note
      );
      await updateDoc(userRef, { notes: updatedNotes });
      setNotes(updatedNotes);
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar nota:', error);
    }
  };

  // Função para formatar a data no padrão brasileiro
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  };

  return (
    <PageLayout
      title="Anotações"
      onAdd={Notes.onAdd}
      customColors={customColors}
      hideLogo={!!embeddedInCentral}
      hideTitle={!!embeddedInCentral}
      hideFooter={!!embeddedInCentral}
      actionButtons={
        isMobile ? (
          <>
            <Box sx={{
              display: 'flex',
              gap: 1,
              width: '100%',
              mb: 1,
              '& .MuiButton-root': {
                flex: 1,
                minWidth: 0,
                px: 1.5,
                fontSize: '0.75rem'
              }
            }}>
              {!embeddedInCentral && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setNoteToDelete({ title: 'todas as notas' });
                  setShowDeleteDialog(true);
                }}
              >
                Remover Todos
              </Button>
              )}
              {!embeddedInCentral && !hideAddButtonInHeader && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Verifica se o usuário está no plano básico e já atingiu o limite
                    if (hasReachedLimit(userPlan, 'maxNotes', notes.length)) {
                      setShowPlanDialog(true);
                      return;
                    }
                    Notes.onAdd();
                  }}
                  sx={{
                    bgcolor: customColors?.primary || 'primary.main',
                    color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: customColors?.primary || 'primary.main',
                      opacity: 0.9
                    }
                  }}
                >
                  Nova Nota
                </Button>
              )}
            </Box>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                displayEmpty
                size="small"
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="">Todas as categorias</MenuItem>
                <Divider />
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Box sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              minWidth: 0,
              px: 1.5,
              fontSize: '1rem'
            }
          }}>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                displayEmpty
                size="small"
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="">Todas as categorias</MenuItem>
                <Divider />
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!embeddedInCentral && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                setNoteToDelete({ title: 'todas as notas' });
                setShowDeleteDialog(true);
              }}
            >
              Remover Todos
            </Button>
            )}
            {!embeddedInCentral && !hideAddButtonInHeader && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Verifica se o usuário está no plano básico e já atingiu o limite
                  if (hasReachedLimit(userPlan, 'maxNotes', notes.length)) {
                    setShowPlanDialog(true);
                    return;
                  }
                  Notes.onAdd();
                }}
                sx={{
                  bgcolor: customColors?.primary || 'primary.main',
                  color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                  '&:hover': {
                    bgcolor: customColors?.primary || 'primary.main',
                    opacity: 0.9
                  }
                }}
              >
                Nova Nota
              </Button>
            )}
          </Box>
        )
      }
    >
      {/* Banner informativo do plano */}
      <PlanInfoBanner
        feature="maxNotes"
        featureName="notas"
        featureNameSingular="nota"
        currentCount={notes.length}
        customColors={customColors}
        onUpgradeClick={() => setShowPlanDialog(true)}
      />

      {/* Filtro por pilar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          pl: { xs: 1, sm: 0 }
        }}
      >
        <Button
          size="small"
          variant={pillarFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setPillarFilter('all')}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 999,
            bgcolor: pillarFilter === 'all'
              ? (customColors?.primary || theme.palette.primary.main)
              : 'transparent',
            color: pillarFilter === 'all'
              ? (theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText))
              : theme.palette.text.primary,
            '&:hover': {
              bgcolor: pillarFilter === 'all'
                ? (customColors?.primary || theme.palette.primary.main)
                : alpha(theme.palette.text.primary, 0.06)
            }
          }}
        >
          Todos
        </Button>
        {PILLARS.map(pillar => {
          const color = getPillarColor(pillar.id, customColors);
          const selected = pillarFilter === pillar.id;
          return (
            <Button
              key={pillar.id}
              size="small"
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setPillarFilter(pillar.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 999,
                borderColor: color,
                bgcolor: selected ? color : 'transparent',
                color: selected ? getContrastText(color) : color,
                '&:hover': {
                  bgcolor: selected ? color : alpha(color, 0.08)
                }
              }}
            >
              {pillar.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={currentFilter}
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 1
            },
            '& .MuiTab-root': {
              borderRadius: 1,
              minHeight: 'auto',
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2,
              py: 1
            },
            '& .MuiTabs-indicator': {
              backgroundColor: customColors?.primary || 'primary.main'
            },
            '& .Mui-selected': {
              color: `${customColors?.primary || 'primary.main'} !important`
            }
          }}
        >
          <Tab label="Todas" value="all" />
          <Tab label="Importantes" value="important" />
          <Tab label="Arquivadas" value="archived" />
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {getFilteredNotes().map((note) => {
          const pillarColor = getPillarColor(note.pillar, customColors);
          const pillarLabel = getPillarLabel(note.pillar);
          return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={note.id}>
            <Tooltip
              title={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {note.title}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {note.content}
                  </Typography>
                  {note.category && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                      Categoria: {note.category}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    Pilar: {pillarLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    Data: {formatDate(new Date(note.date))}
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
              enterDelay={300}
              leaveDelay={0}
            >
              <Card 
                onClick={() => handleEditNote(note)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${pillarColor}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ 
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.2,
                      flex: 1
                    }}>
                      {note.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {note.isImportant && (
                        <StarIcon sx={{ 
                          fontSize: '1rem',
                          color: 'warning.main'
                        }} />
                      )}
                      {note.isArchived && (
                        <ArchiveIcon sx={{ 
                          fontSize: '1rem',
                          color: 'text.secondary'
                        }} />
                      )}
                    </Box>
                  </Box>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {note.content}
                  </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {note.category && (
                    <Chip
                      icon={<LabelIcon sx={{ fontSize: '0.875rem' }} />}
                      label={note.category}
                      size="small"
                      sx={{ 
                        height: 24,
                        '& .MuiChip-label': { 
                          px: 1,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {formatDate(new Date(note.date))}
                  </Typography>
                </Box>
              </CardContent>

              <Box 
                sx={{ 
                  px: 1.5, 
                  pt: 1, 
                  pb: 0.5, 
                  borderTop: 0, 
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'center'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Chip
                  size="small"
                  label={pillarLabel}
                  sx={{
                    bgcolor: pillarColor,
                    color: getContrastText(pillarColor),
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                />
              </Box>

              <CardActions 
                onClick={(e) => e.stopPropagation()}
                sx={{ 
                  justifyContent: 'center',
                  gap: 1,
                  p: 1,
                  borderTop: 1,
                  borderColor: 'divider'
                }}
              >
                <Tooltip title="Marcar como importante">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleImportant(note.id);
                    }}
                    sx={{
                      color: note.isImportant ? 'warning.main' : 'action.active'
                    }}
                  >
                    {note.isImportant ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditNote(note);
                  }}
                  sx={{
                    color: customColors?.primary || 'primary.main'
                  }}
                >
                  <EditIcon />
                </IconButton>
                <Tooltip title={note.isArchived ? "Desarquivar" : "Arquivar"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleArchive(note.id);
                    }}
                    sx={{
                      color: note.isArchived ? customColors?.primary || 'primary.main' : 'action.active'
                    }}
                  >
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(note);
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
              </Card>
                </Tooltip>
              </Grid>
          );
        })}
      </Grid>

      {/* Dialog para adicionar/editar nota */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNote ? 'Editar Nota' : 'Nova Nota'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Título"
              fullWidth
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              error={errors.title}
              helperText={errors.title ? 'O título é obrigatório' : ''}
              required
            />
            <TextField
              label="Conteúdo"
              fullWidth
              multiline
              rows={4}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              error={errors.content}
              helperText={errors.content ? 'O conteúdo é obrigatório' : ''}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Categoria"
              >
                <MenuItem value="">
                  <em>Nenhuma</em>
                </MenuItem>
                {categories.map((category) => {
                  const isDefaultCategory = DEFAULT_CATEGORIES.includes(category);
                  return (
                    <MenuItem 
                      key={category} 
                      value={category}
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span>{category}</span>
                      {!isDefaultCategory && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteCategory(category);
                          }}
                          sx={{ 
                            ml: 1,
                            color: 'error.main',
                            '&:hover': {
                              bgcolor: 'error.light',
                              color: 'error.dark'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </MenuItem>
                  );
                })}
                <Divider />
                <MenuItem 
                  sx={{ p: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box sx={{ p: 1, width: '100%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Nova categoria..."
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddCategory();
                            }}
                            disabled={!newCategory.trim()}
                          >
                            <AddIcon />
                          </IconButton>
                        )
                      }}
                    />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Pilar</InputLabel>
              <Select
                value={selectedPillar}
                onChange={(e) => setSelectedPillar(e.target.value)}
                label="Pilar"
              >
                {PILLARS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                inputFormat="dd/MM/yyyy"
                mask="__/__/____"
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      placeholder: "dd/mm/aaaa"
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            sx={{ bgcolor: customColors?.primary || 'primary.main' }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {noteToDelete?.title === 'todas as notas'
              ? "Tem certeza que deseja remover todas as notas? Esta ação não pode ser desfeita."
              : `Tem certeza que deseja excluir a nota "${noteToDelete?.title}"? Esta ação não pode ser desfeita.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Diálogo de comparação de planos */}
      <PlanComparisonDialog
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        feature="maxNotes"
        featureName="notas"
        currentCount={notes.length}
        customColors={customColors}
      />
    </PageLayout>
  );
} 